// [<agent>] > [Runs] > [Scheduled] > one run.
// docs/Y2026-Q3/design-portfolio-daily-runs.md section 12.
//
//
// No completion gate - a pass commits per project as it goes - but the staged
// changes, live turn and transcript reuse the project panel's own components so
// the two surfaces can't drift on what the agent did.
import { useComposerQuote } from "../components/useComposerQuote";
import { useEffect, useMemo, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useKindeAuth } from "@kinde-oss/kinde-auth-react";

import { ViewAiTraceButton } from "../api";
import {
  useStatefulRun,
  useStatefulRunTurn,
  useStopPass,
} from "../api/hooks/useStatefulAgents";
import { useCancelOperation } from "../api/hooks/useAsyncOperation";
import { useProjectsList } from "../api/hooks/useProjectsList";
import { ProjectPicker } from "../components/ProjectPicker";
import { projectDisplayName } from "../utils/projectName";
import { filterRunByProject, runProjects } from "../utils/runProjectFilter";
import { formatWhen } from "../utils/formatWhen";
import { passRunTimeLabel } from "../utils/runStatus";

import {
  RunDriveDocuments,
  RunGeneratedFiles,
  RunSpendText,
} from "../components/RunView";
import { LivePassPanel, LiveTurnPanel } from "../components/RunLivePanels";
import { StagedChanges } from "../components/RunStagedChanges";
import { RunTranscript } from "../components/RunTranscript";
import { RunComposer } from "../components/RunComposer";
import { ScrollToLatest } from "../components/ScrollToLatest";
import { leftAfterSend } from "../utils/leftAfterSend";
import { useOpenAtLatest } from "../utils/scrollToLatest";
import { useAttachments } from "../api/hooks/useAttachments";
import { RunTitleBar } from "../components/RunTitleBar";
import { appliedRuleChanges } from "../utils/appliedRuleChanges";
import {
  agentDisplayLabel,
  runParticipantsLabel,
  runStarterLabel,
} from "../utils/runParticipants";
import { projectScheduledPath, runPath } from "./learningAgentPaths";
import { useScrollToHash } from "../utils/useScrollToHash";
import styles from "./AgentRunDetailRoute.module.css";

/** `projectId` pins the page to one project's slice of the run, as the project hub
 * opens it; without it the viewer picks which of the run's projects to show. */
export const AgentScheduledRunDetailRoute = ({
  projectId,
}: {
  projectId: string | null;
}) => {
  const { runId } = useParams<{ runId: string }>();
  const decodedRun = runId ? decodeURIComponent(runId) : "";
  const { user } = useKindeAuth();
  const [message, setMessage] = useState("");
  const [sendError, setSendError] = useState<string | null>(null);
  const [stopRequested, setStopRequested] = useState(false);
  const composerRef = useRef<HTMLTextAreaElement>(null);
  const appendQuote = useComposerQuote(setMessage, composerRef);
  const cancelOp = useCancelOperation();
  const stopPass = useStopPass(decodedRun);
  const [passStopRequested, setPassStopRequested] = useState(false);
  // A sweep belongs to no single project, so the turn is scoped by the run
  // alone - the project id here only keys the list this would invalidate.
  const sendTurn = useStatefulRunTurn(decodedRun, "");
  const attachments = useAttachments();
  const contentRef = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const { clear: clearAttachments } = attachments;
  // One element serves every conversation, so a switch must not carry them across.
  useEffect(() => clearAttachments(), [decodedRun, clearAttachments]);
  const detailQuery = useStatefulRun(decodedRun || null);
  const detail = detailQuery.data;
  useScrollToHash(!!detail);
  useOpenAtLatest({ contentRef, barRef, conversationId: decodedRun, ready: !!detail });
  const [picked, setPicked] = useState<string[]>([]);
  const allProjects = useProjectsList().data;
  const options = useMemo(
    () =>
      runProjects(detail?.messages ?? [], detail?.projects ?? [], (id) => {
        const p = allProjects?.find((row) => row.project_id === id);
        return p ? projectDisplayName(p) : id;
      }),
    [detail?.messages, detail?.projects, allProjects],
  );
  const shown = projectId
    ? new Set([projectId])
    : picked.length
      ? new Set(picked)
      : null;
  const visible = filterRunByProject(
    detail?.messages ?? [],
    detail?.projects ?? [],
    shown,
  );
  const hiddenCount = projectId
    ? 0
    : options.filter((o) => shown && !shown.has(o.id)).length;
  // Back to whichever tab this run belongs to. The page serves both a
  // scheduled sweep and an agent conversation - same transcript, same trace,
  // same composer - so only the return path differs.
  // A learning review is the platform's, like a pass: it lives on the Scheduled tab.
  const scheduledKind =
    detail?.run?.kind === "scheduled" || detail?.run?.kind === "learning_review";
  const backTab = scheduledKind ? "scheduled" : "conversations";
  const backLabel = projectId
    ? "Back to this site's scheduled runs"
    : scheduledKind
      ? "Back to scheduled runs"
      : "Back to conversations";
  const backHref = projectId
    ? projectScheduledPath(projectId)
    : runPath(backTab);
  const navigate = useNavigate();
  // Flattened across the transcript rather than per message: an operator asking
  // "what did this run change?" wants one list, not to scan every turn for it.
  const rulesChanged = appliedRuleChanges(
    (detail?.messages ?? []).flatMap((m) => m.turn_flags?.rules_applied ?? []),
  );
  const running =
    !!detail &&
    (!!detail.turn ||
      !!detail.run.active_turn_operation_key ||
      !!detail.pass_running);
  // A turn holds the conversation: what is sent now waits for it. A scheduled pass still
  // sweeping takes no message at all.
  const queueing = running && !detail?.pass_running;
  const passRunTime = detail
    ? passRunTimeLabel(
        { ...detail.run, pass_running: detail.pass_running },
        Date.now(),
      )
    : null;
  const liveTurn = detail?.turn ?? null;
  // Remembered across polls: between two sites' turns the feed reads as idle, and
  // the count should not blink out each time.
  const [sitesTotal, setSitesTotal] = useState(0);
  const feedSites = detail?.pass_feeds?.[0]?.sites ?? 0;
  useEffect(() => {
    if (feedSites > 0) setSitesTotal(feedSites);
  }, [feedSites]);
  // A site is done once the Manager has filed its note for it.
  const sitesDone = new Set(
    (detail?.messages ?? [])
      .filter(
        (m) => m.role === "agent" && m.agent === "manager" && m.project_id,
      )
      .map((m) => m.project_id),
  ).size;
  const stopping = stopRequested || Boolean(liveTurn?.cancel_requested);
  // A hidden run and a learning review take no reply, so nothing quotes into one.
  const canReply = !!detail && !detail.run.hidden_at && detail.run.kind !== "learning_review";

  const send = () => {
    const content = message.trim();
    if (!content || sendTurn.isLoading || (running && !queueing)) return;
    if (attachments.uploading || attachments.failed) return;
    const sent = attachments.ready;
    const atSend = message;
    setSendError(null);
    sendTurn.mutate(
      { content, actor_email: user?.email ?? "", ...sent },
      {
        onSuccess: () => {
          setMessage((m) => leftAfterSend(m, atSend));
          attachments.release(sent);
        },
        // Never fail silently: without this the box just stops responding and the
        // operator cannot tell whether it sent.
        onError: (error) =>
          setSendError(error.message || "Could not send that message."),
      },
    );
  };

  const onStop = async () => {
    if (!liveTurn || stopRequested) return;
    setSendError(null);
    // Optimistic; the poll's cancel_requested is what keeps it durable across a
    // reload while the agent winds down to a graceful summary.
    setStopRequested(true);
    try {
      await cancelOp.mutateAsync({ operation_key: liveTurn.operation_key });
    } catch (e) {
      setStopRequested(false);
      setSendError(
        e instanceof Error ? e.message : "Could not stop the agent.",
      );
    }
  };

  const onStopPass = async () => {
    if (passStopRequested) return;
    setSendError(null);
    setPassStopRequested(true);
    try {
      const result = await stopPass.mutateAsync();
      if (result.status === "finished") {
        // Nothing left to stop: show the run as it ended rather than "Stopping…".
        setPassStopRequested(false);
        void detailQuery.refetch();
      }
    } catch (e) {
      setPassStopRequested(false);
      setSendError(
        e instanceof Error ? e.message : "Could not stop the run.",
      );
    }
  };

  if (detailQuery.isLoading) {
    return <p className={styles.muted}>Loading run…</p>;
  }
  // A failed REFETCH keeps `data` beside `error`, so an error only takes over the
  // screen when we hold no run at all - one 5xx must not replace a live transcript.
  if (!detail) {
    const failed = Boolean(detailQuery.error);
    return (
      <div className={styles.page}>
        <p className={styles.error}>
          {failed
            ? `Could not load this run. ${
                detailQuery.error instanceof Error
                  ? detailQuery.error.message
                  : "The request failed."
              }`
            : "This run was not found."}
        </p>
        <div className={styles.detailHeader}>
          {failed && (
            <button
              type="button"
              className={styles.secondary}
              disabled={detailQuery.isFetching}
              onClick={() => void detailQuery.refetch()}
            >
              {detailQuery.isFetching ? "Retrying…" : "Try again"}
            </button>
          )}
          <Link className={styles.link} to={backHref}>
            {backLabel}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.runWithBar}>
      <div ref={contentRef} className={styles.runWithBarInner}>
        <header className={styles.detailHeader}>
          <div>
            {scheduledKind ? (
              <h2>{detail.run.title || "Untitled run"}</h2>
            ) : (
              <RunTitleBar
                run={detail.run}
                onDeleted={() => navigate(backHref)}
              />
            )}
            <p className={styles.muted}>
              {detail.run.kind === "scheduled"
                ? "Scheduled run"
                : detail.run.kind === "learning_review"
                  ? "Weekly learning review"
                  : "Conversation"}{" "}
              · started {formatWhen(detail.run.created_at)}
              {" · started by "}
              {runStarterLabel(detail.run)}
              {detail.run.started_from && <> from the {detail.run.started_from} page</>}
              {runParticipantsLabel(detail.run) && (
                <> · with {runParticipantsLabel(detail.run)}</>
              )}
              {running ? (
                // Said in words rather than the "active" status token.
                <>
                  {" · "}
                  <span className={styles.runningNow}>● Running now</span>
                  {sitesTotal > 0 &&
                    ` · ${sitesDone} of ${sitesTotal} sites done`}
                </>
              ) : detail.run.status === "interactive" ? (
                // A finished pass someone has re-opened.
                " · finished, and open for questions"
              ) : (
                " · finished"
              )}
              {passRunTime && ` · run time ${passRunTime}`}
              <RunSpendText usd={detail.run.spend_usd} prefix=" · AI spend " />
            </p>
          </div>
          <div className={styles.detailHeaderActions}>
            {/* A pass holds no turn, so LiveTurnPanel's Stop never shows for one. */}
            {detail.run.kind === "scheduled" && detail.pass_running && !liveTurn && (
              <button
                type="button"
                className={styles.secondary}
                disabled={passStopRequested}
                onClick={() => void onStopPass()}
              >
                {passStopRequested ? "Stopping…" : "Stop run"}
              </button>
            )}
            {/* ONE trace for the whole pass, keyed on the run - the same key the
                sweep's single ai_invocation row carries, so this and the button on
                /costs open the same thing. */}
            <ViewAiTraceButton
              surface="stateful_agent_runs"
              artefactId={detail.run.run_id}
              hasTrace={detail.messages.some((m) => m.has_ai_trace)}
              label="View AI trace"
            />
            {projectId && (
              <Link
                className={styles.link}
                to={runPath("scheduled", detail.run.run_id)}
              >
                View the full run
              </Link>
            )}
            <Link className={styles.link} to={backHref}>
              {backLabel}
            </Link>
          </div>
        </header>

        {projectId ? (
          <p className={styles.muted}>
            Showing this site only. The run worked {options.length} project
            {options.length === 1 ? "" : "s"}.
          </p>
        ) : (
          options.length > 1 && (
            <div className={styles.projectFilter}>
              <span>
                Show projects:
                <ProjectPicker
                  options={options}
                  projectIds={picked}
                  placeholder={`All ${options.length} projects`}
                  onChange={setPicked}
                />
              </span>
              {hiddenCount > 0 && (
                <p className={styles.muted}>
                  {hiddenCount} project{hiddenCount === 1 ? "" : "s"} hidden
                </p>
              )}
            </div>
          )
        )}

        {detail.messages.length === 0 ? (
          // A pass that ENDED with no exchanges is a fault, not a "yet".
          <p className={styles.muted}>
            {running
              ? "Still reading the first site - nothing finished yet."
              : "This run ended without working a site."}
          </p>
        ) : (
          <RunTranscript
            runId={detail.run.run_id}
            runKind={detail.run.kind}
            // The run's own project, not the page's: a pass cut down to one site still
            // spans several, and its replies carry their own.
            projectId={detail.run.context_project_id ?? null}
            seen
            messages={visible.messages}
            projects={visible.views}
            draftStatus={detail.draft_status}
            onReply={canReply ? appendQuote : null}
            openResponses={!!projectId}
            openEmails={false}
          />
        )}

        <RunGeneratedFiles
          runId={detail.run.run_id}
          artifacts={detail.artifacts ?? []}
        />

        <RunDriveDocuments documents={detail.drive_documents ?? []} />

        {/* The evidence behind the reply's prose: the full updated text. */}
        {detail.proposal &&
          (detail.proposal.state_changes.length > 0 ||
            detail.proposal.instruction_changes.length > 0) && (
            <section className={styles.draftProposal}>
              <h3>Changes this conversation has staged</h3>
              <StagedChanges proposal={detail.proposal} ticking={null} />
              <p className={styles.muted}>
                Held on the run, not written yet.
                {rulesChanged.length > 0 &&
                  " Rule changes the agent applied as it went are listed separately below - those are already live."}
              </p>
            </section>
          )}

        {/* Below the conversation: the agent explains these in its reply; this is
            the evidence for when an operator wants to check. */}
        {rulesChanged.length > 0 && (
          <details className={styles.appliedChanges}>
            <summary>
              Instruction changes this run applied ({rulesChanged.length})
            </summary>
            <ul>
              {rulesChanged.map((rule) => (
                <li key={rule.id || rule.title}>
                  {rule.id
                    ? `${rule.id} - ${rule.title} (${rule.verbs})`
                    : rule.title}
                </li>
              ))}
            </ul>
            <p className={styles.muted}>
              Applied already - the agent changes its own rules while you are in
              the conversation. The full text of each is on the Prompt tab.
            </p>
          </details>
        )}

        {/* Last before the composer, whatever else the run shows: after sending, the
            operator is at the foot of the page, and a panel above that is one they
            never see. */}
        {liveTurn && (
          <LiveTurnPanel
            turn={liveTurn}
            label={
              detail.run.kind === "scheduled"
                ? "Working through the sites - each appears above as it is finished."
                : "The agent is working…"
            }
            stopping={stopping}
            onStop={() => void onStop()}
            fallbackAgent={agentDisplayLabel(detail.run.agent ?? "")}
          />
        )}
        {/* A pass works up to two groups of sites at once: one panel per lane. */}
        {!liveTurn &&
          running &&
          (detail.pass_feeds ?? []).map((feed) => (
            <LivePassPanel key={feed.project_id} feed={feed} />
          ))}
      </div>

      {/* A finished pass is still a conversation. Replying re-opens it rather
          than starting a fresh run, so the agent answers with the transcript
          and traces above already in hand. */}
      <div ref={barRef} className={styles.bottomBar}>
        <ScrollToLatest contentRef={contentRef} barRef={barRef} />
        {canReply && (
          <RunComposer
            value={message}
            onChange={setMessage}
            onSend={send}
            placeholder="Ask about this run, or tell the agent what it got wrong…"
            // Not while the pass is still being worked: the server refuses a message then.
            disabled={Boolean(running) && !queueing}
            queueing={queueing}
            sending={sendTurn.isLoading}
            textareaRef={composerRef}
            attachments={attachments}
          />
        )}
        {sendError && <p className={styles.error}>{sendError}</p>}
      </div>
    </div>
  );
};

// [Project] > [Conversations] > [Debug: Scheduled] > one run, cut down to this project.
export const ProjectScheduledRunDetailRoute = () => {
  const { projectId } = useParams<{ projectId: string }>();
  return (
    <AgentScheduledRunDetailRoute
      projectId={decodeURIComponent(projectId ?? "")}
    />
  );
};
